import React, { useContext, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import InputAdornment from '@mui/material/InputAdornment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import * as R from 'ramda';
import { GT_ZERO, REQUIRED } from 'utils/helpers/validationMessages';
import { useUserRolePermissions } from 'hooks';
import { bool } from 'prop-types';

import renderInput from './renderUtils';
import { CreateReconsignmentContext, initialFilters } from '../Create';
import { useGetProductOriginsQuery } from '../../reconsignmentsEndpoints';

const renderTags = (tags, getTagProps) =>
  tags.map((item, index) => (
    <Chip
      label={item.originName}
      sx={{ borderColor: 'secondary.main', borderRadius: 25 }}
      variant="outlined"
      {...getTagProps({ index })}
    />
  ));

const Filters = ({ isLoading }) => {
  const { filters, info, setFilters, setInfo } = useContext(CreateReconsignmentContext);
  const { currentAffiliate } = useUserRolePermissions();

  const [validation, setValidation] = useState('');

  // validateVolume :: Number -> String
  const validateVolume = R.cond([
    [R.equals(NaN), R.always(REQUIRED)],
    [R.gte(0), R.always(GT_ZERO)],
    [R.T, R.always('')],
  ]);

  const handleChangeInfo = () =>
    setInfo(
      R.evolve(
        {
          fromLocations: R.always([]),
          souceLocations: R.always([]),
          toLocations: R.always([]),
        },
        info
      )
    );

  const handleVolumeChange = R.compose(
    handleChangeInfo,
    setFilters,
    R.assoc('volume'),
    R.tap(R.compose(setValidation, validateVolume)),
    parseInt,
    R.path(['target', 'value'])
  );

  const params = {
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    productId: R.path(['product', 'productId'], info),
  };

  const { data = [], isFetching } = useGetProductOriginsQuery(
    { params },
    { skip: R.or(R.isNil(currentAffiliate), R.isNil(R.prop('product', info))) }
  );

  return (
    <Accordion defaultExpanded={false} elevation={20} sx={{ m: 0 }}>
      <AccordionSummary
        data-testid="expand-location-filters"
        expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
        sx={{
          borderBottom: '1px solid',
          borderBottomColor: 'graphic.light',
          borderTop: '1px solid',
          borderTopColor: 'graphic.light',
          px: 2,
        }}
      >
        <Typography variant="h6">Source Location Filters</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: 'surface.white', p: 2 }}>
        <Stack spacing={2}>
          <Button
            onClick={() => {
              setFilters(initialFilters);
              handleChangeInfo();
            }}
            sx={{ alignSelf: 'flex-end' }}
            variant="text"
          >
            Clear Filters
          </Button>
          <Typography sx={{ color: 'text.secondary' }} variant="body2">
            Provide the following information to further filter your source location results.
          </Typography>
          <Typography sx={{ color: 'text.primary' }} variant="subtitle1">
            Volume
          </Typography>
          <TextField
            className="css-heap-redact"
            error={R.not(R.isEmpty(validation))}
            helperText={
              R.prop('volume', validation) || 'Will return volumes greater or equal to input'
            }
            id="volume"
            label="Volume"
            onChange={handleVolumeChange}
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">bbl</InputAdornment>,
              },
            }}
            value={R.defaultTo('', R.prop('volume', filters))}
          />
          <Typography sx={{ color: 'text.primary' }} variant="subtitle1">
            Search Source Parameters
          </Typography>
          {R.not(R.prop('isRecurring', info)) ? (
            <Autocomplete
              disabled={R.or(isLoading, R.isNil(R.prop('product', info)))}
              getOptionLabel={o => R.defaultTo('', R.prop('originName', o))}
              isOptionEqualToValue={R.eqProps('originId')}
              limitTags={1}
              loading={isFetching}
              multiple
              onChange={(_, val) => {
                setFilters(R.assoc('origins', val, filters));
                handleChangeInfo();
              }}
              options={data}
              renderInput={renderInput('Origins')}
              renderTags={renderTags}
              value={R.prop('origins', filters)}
            />
          ) : (
            <Autocomplete
              disabled={R.or(isLoading, R.isNil(R.prop('product', info)))}
              getOptionLabel={o => R.defaultTo('', R.prop('originName', o))}
              isOptionEqualToValue={R.eqProps('originId')}
              loading={isFetching}
              onChange={(_, val) => {
                setFilters(R.assoc('origins', val, filters));
                handleChangeInfo();
              }}
              options={data}
              renderInput={renderInput('Origins')}
              value={R.prop('origins', filters)}
            />
          )}
          <FormControl disabled={isLoading}>
            <FormLabel sx={{ mb: 1 }}>Inventory Status</FormLabel>
            <RadioGroup
              name="inventory-status"
              onChange={(_, value) => {
                setFilters(R.assoc('inventoryStatus', value, filters));
                handleChangeInfo();
              }}
              value={R.prop('inventoryStatus', filters)}
            >
              <FormControlLabel control={<Radio />} label="All" sx={{ height: 28 }} value="ALL" />
              <FormControlLabel
                control={<Radio />}
                label="Available"
                sx={{ height: 28 }}
                value="AVAILABLE"
              />
              <FormControlLabel
                control={<Radio />}
                label="In Transit"
                sx={{ height: 28 }}
                value="INTRANSIT"
              />
            </RadioGroup>
          </FormControl>
          <FormControl disabled={isLoading}>
            <FormLabel sx={{ mb: 1 }}>Controlled Status</FormLabel>
            <RadioGroup
              name="consignment-status"
              onChange={(_, value) => {
                setFilters(R.assoc('consignmentStatus', value, filters));
                handleChangeInfo();
              }}
              value={R.prop('consignmentStatus', filters)}
            >
              <FormControlLabel control={<Radio />} label="All" sx={{ height: 28 }} value="ALL" />
              <FormControlLabel
                control={<Radio />}
                label="Controlled"
                sx={{ height: 28 }}
                value="CONTROLLED"
              />
              <FormControlLabel
                control={<Radio />}
                label="Uncontrolled"
                sx={{ height: 28 }}
                value="UNCONTROLLED"
              />
            </RadioGroup>
          </FormControl>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

Filters.propTypes = {
  isLoading: bool.isRequired,
};

export default Filters;
